import React, { type ReactNode, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

const DISCLAIMER_PAGE_HREF = 'https://www.atlassian.com/trust/atlassian-intelligence';

export const DisclaimerTooltip = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

	const handleTrustDisclaimerClicked = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});
		fireUIAnalytics(analyticsEvent, 'jqlBuilderAiTrustDisclaimer');
	};

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="bottom"
			messageId="jql-builder-with-ai.ui.natural-language-to-jql-wrapper.jql-builder-embedded.search-mode-switcher.disclaimer-tooltip.popup"
			messageType="transactional"
			content={() => (
				<Box xcss={PopupStyles}>
					{formatMessage(messages.aiDisclaimerPopupContent, {
						a: (chunks: ReactNode) => (
							<Link
								href={DISCLAIMER_PAGE_HREF}
								target="_blank"
								onClick={handleTrustDisclaimerClicked}
							>
								{chunks}
							</Link>
						),
					})}
				</Box>
			)}
			trigger={(triggerProps) => (
				<Tooltip content={formatMessage(messages.infoIconTooltip)} position="bottom">
					<Button
						appearance="subtle"
						spacing="compact"
						label={formatMessage(messages.infoIconLabel)}
						aria-label={formatMessage(messages.infoIconLabel)}
						iconBefore={
							<InfoIcon label="" LEGACY_size="small" color={token('color.icon.accent.gray')} />
						}
						onClick={() => setIsPopupOpen(!isPopupOpen)}
						{...triggerProps}
					/>
				</Tooltip>
			)}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.link'),
});

const PopupStyles = xcss({
	color: 'color.text',
	background: 'color.background.input',
	padding: 'space.200',
	borderRadius: 'border.radius',
	boxShadow: 'elevation.shadow.overlay',
});
